import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Medal, Award, ArrowUpRight } from "lucide-react";
import { PLACES } from "../../lib/winners";

const TONE = {
  gold: { ring: "ring-gold-500/60", glow: "shadow-[0_30px_80px_-20px_rgba(201,166,70,0.55)]", chip: "bg-gold-500 text-navy-950", text: "text-gold-400", Icon: Trophy },
  silver: { ring: "ring-slate-300/30", glow: "shadow-[0_24px_60px_-24px_rgba(226,232,240,0.25)]", chip: "bg-slate-200 text-navy-950", text: "text-slate-200", Icon: Medal },
  bronze: { ring: "ring-amber-600/40", glow: "shadow-[0_24px_60px_-24px_rgba(217,119,6,0.35)]", chip: "bg-amber-600 text-white", text: "text-amber-400", Icon: Award },
};

export function WinnerCard({ w, info, featured = false, index = 0 }) {
  const place = PLACES[w.rank];
  const t = TONE[place.tone];
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative flex flex-col overflow-hidden rounded-[28px] bg-navy-900/80 ring-1 backdrop-blur-xl transition-transform duration-300 hover:-translate-y-1.5 ${t.ring} ${t.glow}`}
      data-testid={`winner-card-${w.slug}`}>
      <Link to={`/p/${w.slug}`} className="relative block aspect-[2/1] overflow-hidden" data-testid={`winner-photo-link-${w.slug}`}>
        <img src={w.photo} alt={`${w.name} team on stage at U-Hackathon 2026`} loading="lazy"
          className="h-full w-full object-cover object-bottom transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/10 to-transparent" />
        {featured && <div className="winners-shine absolute inset-0" />}
        <span className={`absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest ${t.chip}`} data-testid={`winner-place-${w.slug}`}>
          <t.Icon size={13} /> {place.label}
        </span>
      </Link>
      <div className="relative -mt-10 flex flex-1 flex-col px-5 pb-5">
        <div className="flex items-end gap-3">
          <img src={w.icon} alt="" className={`${featured ? "h-16 w-16" : "h-14 w-14"} shrink-0 rounded-2xl object-contain shadow-card ring-2 ring-navy-950`} />
          <div className="min-w-0 pb-0.5">
            <h3 className={`truncate font-display font-extrabold leading-none text-white ${featured ? "text-[30px]" : "text-[24px]"}`}>{w.name}</h3>
            <p className="mt-1 truncate text-[13px] text-on-dark-muted">{info?.team ? `by ${info.team}` : "\u00a0"}</p>
          </div>
        </div>
        <p className="mt-3 text-[15px] leading-snug text-white/90 line-clamp-2">{info?.one_liner || "\u00a0"}</p>
        <div className="mt-auto flex items-center justify-between gap-3 pt-5">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-on-dark-muted">Cash prize</p>
            <p className={`font-mono text-[22px] font-extrabold leading-tight ${t.text}`} data-testid={`winner-prize-${w.slug}`}>
              {place.prize} <span className="text-[12px] font-bold">THB</span>
            </p>
          </div>
          <Link to={`/p/${w.slug}`} data-testid={`winner-project-link-${w.slug}`}
            className={`inline-flex items-center gap-1.5 rounded-pill px-4 py-2.5 text-[14px] font-bold transition-colors ${featured ? "bg-gold-500 text-navy-950 hover:bg-gold-400" : "bg-white/10 text-white hover:bg-white/20"}`}>
            See project <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}
